import { DriverModel } from "./Driver";
import { Metrics } from "./Metrics";
import { Vehicle } from "./Vehicle";
import { Bicycle } from "./Bicycle";
import { Wall } from "./Wall";

export interface Signals {
  t: number;
  bp: number;
  bicyclePos: number;
  vehiclePos: number;
  vehicleSpeed: number;
  vehicleAcceleration: number;
  exCue: number;
  exPr: number;
  exPp: number;
  exActivity: number;
  inCue: number;
  inPr: number;
  inPp: number;
  inActivity: number;
  accel: number;
  speed: number;
}

export interface SimParams {
  driverModel: number[];
  vehicle: number[];
  bicycle: number[];
  simulation: number[];
}
export class Simulation {
  dt: number = 0.01; // simulation time step [s]
  t: number = 0; // current simulation time [s]
  duration: number = 10; // simulation duration [s]
  driverModel: DriverModel;
  metrics: Metrics;
  vehicle: Vehicle;
  bicycle: Bicycle;
  wall: Wall = { point1: [1, -1], point2: [20, -1] };
  debug: boolean = false;
  tVisibility: number = NaN;
  tClearedPath: number = NaN;
  signals = new Array<Signals>();

  constructor(
    driverModel: DriverModel,
    metrics: Metrics,
    vehicle: Vehicle,
    bicycle: Bicycle,
    wall?: Wall
  ) {
    this.driverModel = driverModel;
    this.metrics = metrics;
    if (wall != undefined) this.wall = wall;
    if (this.metrics.wall == undefined) this.metrics.wall = this.wall;

    this.vehicle = vehicle;
    this.bicycle = bicycle;
    this.signals.push({
      t: 0,
      bp: 0,
      bicyclePos: this.bicycle.position[0],
      vehiclePos: this.vehicle.position[1],
      vehicleSpeed: this.vehicle.speed,
      vehicleAcceleration: this.vehicle.acceleration,
      exCue: 0,
      exActivity: 0,
      inCue: 0,
      inActivity: 0,
      inPr: 0,
      inPp: 0,
      exPp: 0,
      exPr: 0,
      speed: this.vehicle.speed,
      accel: this.vehicle.acceleration
    });
  }

  generate() {
    // for loop till the end of the simulation duration
    for (let index = 0; index < Math.floor(this.duration / this.dt); index++) {
      this.nextStep(this.t);
      this.t = Math.round((this.t + this.dt) / this.dt) * this.dt;
    }
  }

  nextStep(t: number) {
    let bp = 0;
    // check if the bicycle appeared AND check if the bicycle is in the driver's FOV
    if (
      this.metrics.isBicycleVisible() &&
      Math.abs(this.metrics.getBearingAngle()[0]) < (120 * Math.PI) / (180 * 2)
    ) {
      if (isNaN(this.tVisibility)) this.tVisibility = t;

      if (this.metrics.latDist[1] < 0) {
        if (isNaN(this.tClearedPath)) this.tClearedPath = t;
      }
      this.driverModel.update(
        t,
        this.metrics.getLooming(),
        this.metrics.getPPET(),
        this.metrics.getLatDist(),
        Math.abs(this.bicycle.speed)
      );
      bp = this.driverModel.getBrakeDeflection();
    }
    // FIXME: how to handle when the bicycle is not visible any longer (let's say that the bicycle is out of the sight)
    let looming = this.metrics.getLooming();
    let PPET = this.metrics.getPPET();

    // updates
    this.vehicle.updateKinematics(bp);
    this.bicycle.updatePosition();
    this.metrics.update(this.vehicle, this.bicycle);

    // store data into the signal variable
    this.signals.push({
      t: t,
      bp: bp,
      bicyclePos: this.bicycle.position[0],
      vehiclePos: this.vehicle.position[1],
      vehicleSpeed: this.vehicle.speed,
      vehicleAcceleration: this.vehicle.acceleration,
      exCue: looming,
      exPr: this.driverModel.excitatoryBranch.Pr.getValue(),
      exPp: this.driverModel.excitatoryBranch.Pp.getValue(),
      exActivity: this.driverModel.excitatoryBranch.getValue(),
      inCue: PPET,
      inPr: this.driverModel.inhibitoryBranch.Pr.getValue(),
      inPp: this.driverModel.inhibitoryBranch.Pp.getValue(),
      inActivity: this.driverModel.inhibitoryBranch.getValue(),
      accel: this.vehicle.acceleration,
      speed: this.vehicle.speed
    });
    // if (this.debug)
    // console.log(Math.abs(this.vehicle.speed))
  }
  reinit(simParams: SimParams) {
    // reinit simulation
    this.t = 0;
    this.tVisibility = NaN;
    this.tClearedPath = NaN;
    this.wall.point1[0] = simParams.simulation[0];
    this.dt = simParams.simulation[1];
    this.duration = simParams.simulation[2];
    this.signals = new Array<Signals>();
    this.signals.push({
      t: 0,
      bp: 0,
      bicyclePos: this.bicycle.position[0],
      vehiclePos: this.vehicle.position[1],
      vehicleSpeed: this.vehicle.speed,
      vehicleAcceleration: this.vehicle.acceleration,
      exCue: 0,
      exActivity: 0,
      inCue: 0,
      inActivity: 0,
      inPr: 0,
      inPp: 0,
      exPp: 0,
      exPr: 0,
      speed: this.vehicle.speed,
      accel: this.vehicle.acceleration
    });
    // reinit the other instances
    this.vehicle.reinit(simParams.vehicle, this.dt);
    this.bicycle.reinit(simParams.bicycle, this.dt);
    this.driverModel.reinit(simParams.driverModel, this.dt);
    this.metrics.reset(this.vehicle, this.bicycle, this.wall, this.dt);
  }
}
